package com.elan.edubridge.data.firebase

import com.elan.edubridge.data.firebase.externals.Auth
import com.elan.edubridge.data.firebase.externals.FirebaseStorage
import com.elan.edubridge.data.firebase.externals.Functions
import com.elan.edubridge.data.firebase.externals.httpsCallable
import com.elan.edubridge.data.firebase.externals.ref
import com.elan.edubridge.data.firebase.externals.uploadBytes
import com.elan.edubridge.shared.data.firebase.FirebaseNaming
import com.elan.edubridge.shared.domain.model.AttachmentContentType
import kotlinx.coroutines.await
import org.w3c.files.File

/**
 * 파일을 개인 스테이징 경로에 올린 뒤 소유 문서로 승격한다 — 게시글(`attachPostUpload`)과
 * 과제 자료(`attachAssignmentUpload`)가 같은 거래를 쓴다.
 *
 * Storage 규칙은 `edubridge/uploads/{uid}/{uploadId}`만 직접 쓰기를 허용한다. 따라서 uid는
 * 호출자가 넘기지 않고 현재 Firebase 인증 사용자에게서만 얻고, 업로드 성공 뒤에만 서버가
 * 실제 바이트를 검사·승격한다. 서명 URL이나 fetch를 쓰지 않으므로 브라우저 CORS 설정도 필요 없다.
 */
object AttachmentStagingUploader {
    private var auth: Auth? = null
    private var storage: FirebaseStorage? = null
    private var functions: Functions? = null

    /** [installWebGraph]에서 한 번 초기화한다. UI는 이 객체의 업로드 API만 사용한다. */
    internal fun configure(auth: Auth, storage: FirebaseStorage, functions: Functions) {
        this.auth = auth
        this.storage = storage
        this.functions = functions
    }

    /** 서버 `UPLOAD_ID_PATTERN`과 같은 16자리 hex(정규식 8~64자리 범위 안) 식별자다. */
    fun newUploadId(): String = js("(() => { const bytes = new Uint8Array(16); globalThis.crypto.getRandomValues(bytes); return Array.from(bytes, value => value.toString(16).padStart(2, '0')).join(''); })()") as String

    /**
     * 같은 [uploadId]로 다시 호출하면 승격 전 스테이징 객체를 안전하게 재시도할 수 있다.
     *
     * [PostAttachmentUploadResult.stage]가 [PostAttachmentUploadStage.STAGING]이면 Storage SDK
     * 전송이 실패한 것이고, [PostAttachmentUploadStage.ATTACH]면 전송은 됐지만 서버 승격이
     * 거부된 것이다. 후자의 [errorMessage]는 `not-found`/`failed-precondition`/
     * `invalid-argument` 등 서버 문구를 그대로 보존한다.
     */
    suspend fun uploadAndAttach(
        academyId: String,
        postId: String,
        file: File,
        uploadId: String = newUploadId(),
    ): AttachmentUploadResult = stageAndPromote(
        file = file,
        uploadId = uploadId,
        callable = "attachPostUpload",
        logLabel = "board",
        attachFailureMessage = "첨부 파일을 게시글에 등록하지 못했어요. 다시 시도해 주세요.",
    ) { payload ->
        payload.academyId = academyId
        payload.postId = postId
    }

    /** 과제 자료. 게시글과 같은 스테이징을 쓰고 승격 콜러블만 다르다. */
    suspend fun uploadAndAttachToAssignment(
        academyId: String,
        assignmentId: String,
        file: File,
        uploadId: String = newUploadId(),
    ): AttachmentUploadResult = stageAndPromote(
        file = file,
        uploadId = uploadId,
        callable = "attachAssignmentUpload",
        logLabel = "assignment",
        attachFailureMessage = "자료를 과제에 등록하지 못했어요. 다시 시도해 주세요.",
    ) { payload ->
        payload.academyId = academyId
        payload.assignmentId = assignmentId
    }

    private suspend fun stageAndPromote(
        file: File,
        uploadId: String,
        callable: String,
        logLabel: String,
        attachFailureMessage: String,
        fillTarget: (dynamic) -> Unit,
    ): AttachmentUploadResult {
        val contentType = AttachmentContentType.fromMimeType(file.type)
            ?: return inputRejected(uploadId, "JPEG, PNG, WEBP 또는 PDF만 첨부할 수 있어요.")
        if (file.size.toDouble() >= MAX_ATTACHMENT_BYTES.toDouble()) {
            return inputRejected(uploadId, "첨부 파일은 각각 10MiB보다 작아야 해요.")
        }
        if (!UPLOAD_ID_PATTERN.matches(uploadId)) {
            return inputRejected(uploadId, "첨부 업로드 식별자가 올바르지 않아요. 다시 시도해 주세요.")
        }

        val currentAuth = auth
        val currentStorage = storage
        val currentFunctions = functions
        val uid = currentAuth?.currentUser?.uid
        if (uid == null || currentStorage == null || currentFunctions == null) {
            return AttachmentUploadResult(
                stage = AttachmentUploadStage.STAGING,
                uploadId = uploadId,
                errorMessage = "로그인 상태를 확인하지 못했어요. 다시 로그인해 주세요.",
            )
        }

        val metadata = js("({})")
        metadata.contentType = contentType.mimeType
        try {
            val stagingPath = "${FirebaseNaming.STORAGE_ROOT}/uploads/$uid/$uploadId"
            uploadBytes(ref(currentStorage, stagingPath), file, metadata).await()
        } catch (error: Throwable) {
            // 경로·uid·uploadId는 로그에 남기지 않는다. SDK code만 운영 진단에 충분하다.
            console.error("[EduBridge] $logLabel attachment staging upload failed code=${error.jsErrorCode() ?: "unknown"}")
            return AttachmentUploadResult(
                stage = AttachmentUploadStage.STAGING,
                uploadId = uploadId,
                errorMessage = "첨부 파일을 임시 저장하지 못했어요. 네트워크를 확인한 뒤 다시 시도해 주세요.",
            )
        }

        // dynamic 위의 `.also { }`는 stdlib 확장이 아니라 런타임 JS 멤버 호출로 나가
        // `{}.also is not a function`으로 죽는다(QrRenderer.kt와 같은 함정). 명시 대입만 쓴다.
        val payload: dynamic = js("({})")
        fillTarget(payload)
        payload.uploadId = uploadId
        payload.fileName = file.name
        return try {
            val response = httpsCallable(
                currentFunctions,
                "${FirebaseNaming.FUNCTIONS_PREFIX}$callable",
            )(payload).await()
            val attachmentId = response.data["attachmentId"] as? String
            if (attachmentId == null) {
                console.error("[EduBridge] $logLabel attachment attach returned no attachmentId")
                AttachmentUploadResult(
                    stage = AttachmentUploadStage.ATTACH,
                    uploadId = uploadId,
                    errorMessage = "파일 등록 응답을 확인하지 못했어요. 다시 시도해 주세요.",
                )
            } else {
                AttachmentUploadResult(
                    stage = AttachmentUploadStage.SUCCESS,
                    uploadId = uploadId,
                    attachmentId = attachmentId,
                )
            }
        } catch (error: Throwable) {
            // 서버 HttpsError 메시지는 화면이 not-found·한도 초과·형식 오류를 구분하게 보존한다.
            console.error("[EduBridge] $logLabel attachment attach failed code=${error.jsErrorCode() ?: "unknown"}")
            AttachmentUploadResult(
                stage = AttachmentUploadStage.ATTACH,
                uploadId = uploadId,
                errorCode = error.jsErrorCode()?.substringAfterLast('/'),
                errorMessage = error.jsErrorMessageOrDefault(attachFailureMessage),
            )
        }
    }

    private fun inputRejected(uploadId: String, message: String): AttachmentUploadResult =
        AttachmentUploadResult(
            stage = AttachmentUploadStage.INPUT,
            uploadId = uploadId,
            errorMessage = message,
        )

    private const val MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
    private val UPLOAD_ID_PATTERN = Regex("[A-Za-z0-9_-]{8,64}")
}

/** 업로드 실패 지점이 Storage SDK인지 서버 승격인지 UI가 구분하도록 노출한다. */
enum class AttachmentUploadStage { SUCCESS, INPUT, STAGING, ATTACH }

data class AttachmentUploadResult(
    val stage: AttachmentUploadStage,
    val uploadId: String,
    val attachmentId: String? = null,
    val errorCode: String? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = stage == AttachmentUploadStage.SUCCESS
}
